import { useEffect, useId, useRef, useState } from 'react';

import { CONTRAST_MAX, CONTRAST_MIN, THEMES, setAppearance, useAppearance, type Theme } from '../lib/themes.ts';
import { PaletteIcon } from './icons.tsx';

/**
 * The colour theme and contrast, from the foot of the rail. Both are saved in this browser
 * (lib/themes.ts); the panel opens beside the rail and closes on Escape or a click outside.
 */
export function ThemePicker() {
  const [open, setOpen] = useState(false);
  const current = useAppearance();
  const panelId = useId();
  const sliderId = useId();
  const anchor = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (anchor.current && !anchor.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const contrastWords = current.contrast === 0 ? 'Theme default' : `${current.contrast > 0 ? '+' : ''}${current.contrast}%`;

  return (
    <div className="theme-picker" ref={anchor}>
      <button
        ref={trigger}
        type="button"
        className="nav-item nav-rail-item"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      >
        <PaletteIcon size={16} />
        <span>Theme</span>
      </button>
      {open ? (
        <div className="theme-panel" id={panelId} role="dialog" aria-label="Colour theme and contrast">
          <div className="theme-panel-head">
            <strong>Colour theme</strong>
            <span>Saved in this browser</span>
          </div>
          <div className="theme-list" role="radiogroup" aria-label="Colour theme">
            {THEMES.map((theme) => (
              <button
                key={theme.id}
                type="button"
                role="radio"
                aria-checked={theme.id === current.themeId}
                className="theme-option"
                onClick={() => setAppearance({ themeId: theme.id, contrast: current.contrast })}
              >
                <ThemeSwatch theme={theme} />
                <span className="theme-option-text">
                  <span className="theme-option-name">{theme.name}</span>
                  <span className="theme-option-description">{theme.description}</span>
                </span>
                {theme.id === current.themeId ? <CheckMark /> : null}
              </button>
            ))}
          </div>
          <div className="theme-contrast">
            <div className="theme-contrast-head">
              <label htmlFor={sliderId}>Contrast</label>
              <output htmlFor={sliderId} data-contrast={current.contrast}>{contrastWords}</output>
            </div>
            <input
              id={sliderId}
              type="range"
              min={CONTRAST_MIN}
              max={CONTRAST_MAX}
              step={5}
              value={current.contrast}
              aria-valuetext={contrastWords}
              onChange={(event) => setAppearance({ themeId: current.themeId, contrast: Number(event.target.value) })}
            />
            <div className="theme-contrast-scale">
              <span aria-hidden>Softer</span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={current.contrast === 0}
                onClick={() => setAppearance({ themeId: current.themeId, contrast: 0 })}
              >
                Reset
              </button>
              <span aria-hidden>Sharper</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** A thumbnail in the theme's own colours: its ground, a sidebar, its accent and info hues. */
function ThemeSwatch({ theme }: { theme: Theme }) {
  const p = theme.palette;
  return (
    <span className="theme-swatch" style={{ background: p.surface, boxShadow: `inset 0 0 0 1px ${p.surface3}` }} aria-hidden>
      <span className="theme-swatch-side" style={{ background: p.bgDeep }}>
        <i style={{ background: p.queuedBright }} />
      </span>
      <span className="theme-swatch-lines">
        <i style={{ background: p.accent, width: '62%' }} />
        <i style={{ background: p.info, width: '78%', marginLeft: '22%' }} />
      </span>
    </span>
  );
}

function CheckMark() {
  return (
    <svg className="theme-option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}
